import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const BulkQRUpload = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrItems, setQrItems] = useState([
    { name: '', type: 'url', destination: '' }
  ]);

  const addQRItem = () => {
    setQrItems([...qrItems, { name: '', type: 'url', destination: '' }]);
  };

  const removeQRItem = (index) => {
    setQrItems(qrItems.filter((_, i) => i !== index));
  };

  const updateQRItem = (index, field, value) => {
    const updated = [...qrItems];
    updated[index][field] = value;
    setQrItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const validItems = qrItems.filter(item => item.name && item.destination);
    if (validItems.length === 0) {
      toast.error('Please add at least one QR code with name and destination');
      return;
    }

    setLoading(true);
    try {
      await api.post('/qr/bulk', { qrCodes: validItems });
      toast.success(`Successfully created ${validItems.length} QR codes`);
      setOpen(false);
      setQrItems([{ name: '', type: 'url', destination: '' }]);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating QR codes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-upload-btn">
          <Upload className="h-4 w-4 mr-2" />
          Bulk Create
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">Bulk QR Code Creation</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create multiple QR codes at once (Professional & Enterprise only)
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {qrItems.map((item, index) => (
            <div key={index} className="card p-4 space-y-3 relative">
              {qrItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeQRItem(index)}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                  data-testid={`remove-qr-item-${index}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>QR Code Name</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => updateQRItem(index, 'name', e.target.value)}
                    placeholder={`QR Code ${index + 1}`}
                    required
                    data-testid={`bulk-qr-name-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Type</Label>
                  <select
                    value={item.type}
                    onChange={(e) => updateQRItem(index, 'type', e.target.value)}
                    className="input-base"
                    data-testid={`bulk-qr-type-${index}`}
                  >
                    <option value="url">URL Redirect</option>
                    <option value="content">Content Display</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label>Destination</Label>
                <Input
                  value={item.destination}
                  onChange={(e) => updateQRItem(index, 'destination', e.target.value)}
                  placeholder={item.type === 'url' ? 'https://example.com' : 'Content to display'}
                  required
                  data-testid={`bulk-qr-destination-${index}`}
                />
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addQRItem}
            className="w-full"
            data-testid="add-another-qr-btn"
          >
            + Add Another QR Code
          </Button>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="btn-accent flex-1" disabled={loading} data-testid="bulk-submit-btn">
              {loading ? 'Creating...' : `Create ${qrItems.length} QR Code${qrItems.length > 1 ? 's' : ''}`}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="bulk-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkQRUpload;
